import React from 'react';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import Progress from 'antd/lib/progress';
import Switch from 'antd/lib/switch';
import Tooltip from 'antd/lib/tooltip';
import {
    CheckCircleFilled, ExclamationCircleFilled, LoadingOutlined, MoreOutlined,
} from '@ant-design/icons';

import { t } from 'utils/i18n';
import type { StepState, StepStatus } from './steps-api';
import {
    countLabel, failureTooltip, progressPercent, rowAction,
} from './steps-model';

interface Props {
    step: StepState;
    noun?: 'drawing' | 'photo';
    busy: boolean;
    // why the step cannot run on the chosen drawings yet, e.g. Run “Detect furniture” first
    blocked?: string | null;
    // the step on one drawing: what it did there, without a counter or progress bar
    single?: boolean;
    // the run works on this step on drawings outside the chosen ones: Stop must stay reachable
    runningElsewhere?: boolean;
    onRun(force: boolean): void;
    onRetryFailed?(): void;
    onStop(): void;
    onChooseFunction(functionId: string): void;
    onToggle(enabled: boolean): void;
    // the align row: opens the review of the aligned drawings
    onAlign?(): void;
    // the align row, once drawings were replaced: puts the originals back
    onRestore?(): void;
    children?: React.ReactNode;
}

function StatusIcon({ status }: { status: StepStatus }): JSX.Element {
    if (status === 'done') return <CheckCircleFilled className='cvat-step-icon cvat-step-icon-done' aria-label='done' />;
    if (status === 'running' || status === 'queued') {
        return <LoadingOutlined className={`cvat-step-icon cvat-step-icon-${status}`} aria-label={status} />;
    }
    if (status === 'failed') {
        return <ExclamationCircleFilled className='cvat-step-icon cvat-step-icon-failed' aria-label='failed' />;
    }
    return <span className={`cvat-step-icon cvat-step-icon-${status}`} aria-label={status} role='img' />;
}

export default function StepRow(props: Props): JSX.Element {
    const {
        step, noun = 'drawing', busy, blocked = null, single = false, runningElsewhere = false, onRun, onRetryFailed, onStop,
        onChooseFunction, onToggle, onAlign, onRestore, children,
    } = props;
    const action = rowAction(step);
    const active = step.status === 'running' || step.status === 'queued';
    const choosable = step.kind === 'lambda' && step.available && step.candidates.length > 1 && !active;
    const muted = !step.available || !step.enabled || !!blocked;
    const counted = step.available && step.enabled;

    const menu = (
        <Menu
            selectedKeys={step.function ? [step.function.id] : []}
            onClick={({ key }) => onChooseFunction(String(key))}
        >
            {step.candidates.map((candidate) => (
                <Menu.Item key={candidate.id} disabled={candidate.deployed === false}>
                    {`${candidate.name}${candidate.recommended ? ` · ${t('recommended')}` : ''}${
                        candidate.deployed === false ? ` · ${t('not deployed')}` : ''}`}
                </Menu.Item>
            ))}
        </Menu>
    );

    return (
        <li className={`cvat-step-row cvat-step-row-${step.status}`} data-step={step.id} aria-label={t(step.title)}>
            <div className='cvat-step-row-main'>
                <StatusIcon status={step.status} />
                <Tooltip title={blocked}>
                    <span className={`cvat-step-title${muted ? ' cvat-step-title-muted' : ''}`}>{t(step.title)}</span>
                </Tooltip>
                {step.optional && <span className='cvat-step-optional'>{t('optional')}</span>}
                {step.kind === 'align' && (
                    <Tooltip title={busy ? t('Available when the processing finishes') : null}>
                        <span className='cvat-step-run-wrapper'>
                            <Button
                                size='small'
                                className='cvat-step-align'
                                disabled={busy || !step.available}
                                onClick={onAlign}
                            >
                                {/* as the other steps say: it starts aligning, the review shows the progress */}
                                {step.status === 'done' ? t('Run again') : t('Run')}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {step.optional && (
                    <Switch
                        size='small'
                        checked={step.available && step.enabled}
                        disabled={!step.available || busy}
                        onChange={onToggle}
                        aria-label={t('{title} on', { title: t(step.title) })}
                    />
                )}
                {action && (
                    <Tooltip title={blocked}>
                        {/* a disabled button swallows hover: the wrapper carries the tooltip */}
                        <span className='cvat-step-run-wrapper'>
                            <Button
                                size='small'
                                className='cvat-step-run'
                                type={step.status === 'failed' ? 'primary' : 'default'}
                                disabled={busy || !!blocked}
                                onClick={() => onRun(action.force)}
                            >
                                {t(action.label)}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {step.kind === 'align' && onRestore && (
                    // in the menu, as the model of the other steps: the row has no room beside Run
                    <Dropdown
                        overlay={(
                            <Menu onClick={onRestore}>
                                <Menu.Item key='restore'>{t('Restore originals')}</Menu.Item>
                            </Menu>
                        )}
                        trigger={['click']}
                        disabled={busy}
                    >
                        <Button
                            type='text'
                            size='small'
                            className='cvat-step-more'
                            aria-label={t('More')}
                            icon={<MoreOutlined />}
                        />
                    </Dropdown>
                )}
                {choosable && (
                    <Dropdown overlay={menu} trigger={['click']} disabled={busy}>
                        <Button
                            type='text'
                            size='small'
                            className='cvat-step-more'
                            aria-label={t('Change model')}
                            icon={<MoreOutlined />}
                        />
                    </Dropdown>
                )}
                {!single && <span className='cvat-step-count'>{countLabel(step)}</span>}
            </div>
            {(active || runningElsewhere) && (
                <div className='cvat-step-progress'>
                    {active && !single && <Progress percent={progressPercent(step)} showInfo={false} size='small' />}
                    <div className='cvat-step-progress-meta'>
                        <span>
                            {active ?
                                step.function?.name ?? (step.kind === 'library' ? t('Library match') : '') :
                                (noun === 'photo' ? t('Running on other photos') : t('Running on other drawings'))}
                        </span>
                        <Button type='link' size='small' onClick={onStop}>{t('Stop')}</Button>
                    </div>
                </div>
            )}
            {!step.available && step.reason && <div className='cvat-step-reason'>{t(step.reason)}</div>}
            {counted && single && step.status === 'failed' && step.error && (
                <Tooltip title={<span className='cvat-step-error-tooltip'>{failureTooltip(step)}</span>}>
                    <div className='cvat-step-error'>{t('Failed — {error}', { error: t(step.error) })}</div>
                </Tooltip>
            )}
            {counted && !single && step.counts.failed > 0 && (
                // the error stays in the tooltip: the failures may be old, or on other drawings
                <div className='cvat-step-failures'>
                    <Tooltip
                        title={<span className='cvat-step-error-tooltip'>{failureTooltip(step)}</span>}
                    >
                        <span className='cvat-step-failed-count'>{`${step.counts.failed} failed`}</span>
                    </Tooltip>
                    {onRetryFailed && (
                        <Button type='link' size='small' disabled={busy} onClick={onRetryFailed}>
                            {t('Retry failed')}
                        </Button>
                    )}
                </div>
            )}
            {children}
        </li>
    );
}
